import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import {
  HiOutlinePause,
  HiOutlinePhotograph,
  HiOutlinePlay,
  HiOutlineTrash,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MediaHousePicker } from "./MediaHousePicker";
import { PhonicsLibraryPicker, type PickedPhonics } from "./PhonicsLibraryPicker";
import type { EditableAudio, EditableField, EditableImage, EditableOption } from "./draftFields";

/** An upload reports a src; a field stores the three nullable columns slides keep media in. */
const toStored = (media: { id: string; src: string; filename: string }) => ({
  id: media.id,
  src: media.src,
  filename: media.filename,
});

const inputClass =
  "w-full rounded-md border border-gray-200 bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:border-gray-800";

const labelClass =
  "block text-[10px] font-bold uppercase leading-none tracking-widest text-gray-500 dark:text-gray-400";

function OptionRow({
  option,
  onChange,
  onMarkCorrect,
}: {
  option: EditableOption;
  onChange: (text: string) => void;
  onMarkCorrect: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onMarkCorrect}
        aria-label={option.isCorrect ? "Correct answer" : "Mark as the correct answer"}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
          option.isCorrect
            ? "border-accent-500 bg-accent-500 text-white"
            : "border-gray-300 text-transparent hover:border-accent-500 dark:border-gray-700"
        }`}
      >
        <FaCheck fontSize={10} />
      </button>
      <input
        type="text"
        value={option.text}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      />
    </div>
  );
}

/**
 * One slot for any media the slide holds, picture or sound alike: Media House on click, a local
 * file on drop. Left empty, a sound is spoken from the script instead.
 */
function MediaField({
  field,
  media,
  onChange,
  onPickPhonics,
}: {
  field: EditableField;
  media: EditableImage | EditableAudio;
  onChange: (field: EditableField) => void;
  /** Set on a phonics clip, which is browsed by sound and carries the one it records. */
  onPickPhonics?: ((picked: PickedPhonics) => void) | undefined;
}) {
  const [isPicking, setIsPicking] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<HTMLAudioElement>(null);

  const kind = media.kind;
  const setMedia = (value: { id: string | null; src: string | null; filename: string | null }) =>
    onChange({
      ...field,
      value: kind === "audio" ? { kind, ...value, phonemeId: null } : { kind, ...value },
    });

  const image = useLocalImageUpload((uploaded) => setMedia(toStored(uploaded)));
  const audio = useLocalAudioUpload((uploaded) => setMedia(toStored(uploaded)));
  const upload = kind === "audio" ? audio : image;

  const togglePlay = () => {
    const player = playerRef.current;
    if (!player) return;
    if (player.paused) void player.play();
    else player.pause();
  };

  return (
    <div
      className="relative h-28 w-28 shrink-0"
      onDragOver={(event) => {
        event.preventDefault();
        setIsDraggedOver(true);
      }}
      onDragLeave={() => setIsDraggedOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDraggedOver(false);
        const file = event.dataTransfer.files[0];
        if (file) void upload.handleFileDrop(file);
      }}
    >
      {media.src ? (
        <>
          {kind === "audio" ? (
            <button
              type="button"
              onClick={togglePlay}
              className={`flex h-28 w-28 flex-col items-center justify-center gap-1.5 rounded-lg border px-2 ${
                isDraggedOver ? "border-primary-500" : "border-gray-200 dark:border-gray-800"
              }`}
            >
              {isPlaying ? (
                <HiOutlinePause className="h-6 w-6 text-primary-500" />
              ) : (
                <HiOutlinePlay className="h-6 w-6 text-primary-500" />
              )}
              <span className="line-clamp-2 break-all text-[10px] text-gray-500 dark:text-gray-400">
                {media.filename}
              </span>
              {/* biome-ignore lint/a11y/useMediaCaption: a sound effect has nothing to caption */}
              <audio
                ref={playerRef}
                src={media.src}
                preload="none"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
            </button>
          ) : (
            <img
              src={media.src}
              alt={field.label}
              className={`h-28 w-28 rounded-lg border object-cover ${
                isDraggedOver ? "border-primary-500" : "border-gray-200 dark:border-gray-800"
              }`}
            />
          )}
          <button
            type="button"
            onClick={() => setMedia({ id: null, src: null, filename: null })}
            aria-label={`Remove ${field.label}`}
            className="absolute -right-1.5 -top-1.5 rounded-full border border-gray-200 bg-background p-1 text-gray-500 hover:text-error-500 dark:border-gray-800"
          >
            <HiOutlineTrash className="h-3 w-3" />
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={upload.isUploading}
          onClick={() => setIsPicking(true)}
          className={`flex h-28 w-28 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[10px] uppercase tracking-wide disabled:opacity-60 ${
            isDraggedOver
              ? "border-primary-500 text-primary-500"
              : "border-gray-300 text-gray-500 hover:border-primary-500 hover:text-primary-500 dark:border-gray-700"
          }`}
        >
          {kind === "audio" ? (
            <HiOutlineVolumeUp className="h-6 w-6" />
          ) : (
            <HiOutlinePhotograph className="h-6 w-6" />
          )}
          {upload.isUploading ? "Uploading…" : field.label}
        </button>
      )}
      {isPicking &&
        (onPickPhonics ? (
          <PhonicsLibraryPicker
            isOpen={isPicking}
            onClose={() => setIsPicking(false)}
            onPick={onPickPhonics}
          />
        ) : (
          <MediaHousePicker
            isOpen={isPicking}
            onClose={() => setIsPicking(false)}
            onPick={(picked) => setMedia(picked)}
            mediaType={kind}
          />
        ))}
    </div>
  );
}

export function DraftFieldEditor({
  field,
  onChange,
  onPickPhonics,
}: {
  field: EditableField;
  onChange: (field: EditableField) => void;
  onPickPhonics?: ((picked: PickedPhonics) => void) | undefined;
}) {
  const { value } = field;

  if (value.kind === "image" || value.kind === "audio") {
    return (
      <MediaField field={field} media={value} onChange={onChange} onPickPhonics={onPickPhonics} />
    );
  }

  if (value.kind === "options") {
    const setOptions = (options: EditableOption[]) =>
      onChange({ ...field, value: { kind: "options", options } });

    return (
      <div className="space-y-1">
        <span className={labelClass}>{field.label}</span>
        <div className="space-y-1">
          {value.options.map((option, index) => (
            <OptionRow
              key={`${field.key}-${index}`}
              option={option}
              onChange={(text) =>
                setOptions(value.options.map((one, at) => (at === index ? { ...one, text } : one)))
              }
              onMarkCorrect={() =>
                setOptions(value.options.map((one, at) => ({ ...one, isCorrect: at === index })))
              }
            />
          ))}
        </div>
      </div>
    );
  }

  const setText = (text: string) => onChange({ ...field, value: { ...value, text } });

  // A passage runs to a dozen lines where a question runs to one, so the box follows its content;
  // capped so one long passage still leaves the questions under it in view.
  const rows = Math.min(Math.max(value.text.split("\n").length, 2), 14);

  return (
    <label className="block space-y-1">
      <span className={labelClass}>{field.label}</span>
      {value.kind === "multiline" ? (
        <textarea
          rows={rows}
          value={value.text}
          onChange={(event) => setText(event.target.value)}
          className={inputClass}
        />
      ) : (
        <input
          type="text"
          value={value.text}
          onChange={(event) => setText(event.target.value)}
          className={inputClass}
        />
      )}
    </label>
  );
}
